import { PageHeader, usePageHeader } from '@easydoctor/ui';
import { ROLE_LABELS, TEAM_LEVEL_LABELS } from '@easyhelp/shared';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Snackbar from '@mui/material/Snackbar';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useState } from 'react';
import { useSession } from '../../api/auth';
import { useOwnMcpConnections, useRevokeOwnMcpConnection } from '../../api/mcp-connections';
import { McpConnectionsPanel } from '../../components/mcp';
import { HelpButton } from '../../guida/HelpButton';

/** Le connessioni MCP proprie: lette solo con l'MCP acceso, quindi in un componente a parte. */
function OwnMcpConnections({ onRevoked }: { onRevoked: (message: string) => void }) {
  const query = useOwnMcpConnections();
  const revoke = useRevokeOwnMcpConnection();
  return <McpConnectionsPanel owner={{ kind: 'own' }} query={query} revoke={revoke} onRevoked={onRevoked} />;
}

/**
 * Profilo (PRD-007 US-2, #152, PAGE-012): chi è collegato, con quale ruolo e livello, e le proprie
 * connessioni di Claude e Cursor con «Revoca». Per ogni ruolo; la sezione delle connessioni c'è solo
 * se l'istanza ha l'MCP acceso (`features.mcp` della sessione). Sta dentro `RequireSession`.
 */
export function ProfiloPage() {
  usePageHeader({ title: 'Profilo' });
  const { data: session } = useSession();
  const [notice, setNotice] = useState<string | null>(null);
  const member = session?.member;
  // Un'API precedente a #152 (rilascio in corso) non manda `features`: vale come MCP spento.
  const mcp = session?.features?.mcp ?? false;

  return (
    <Stack spacing={4}>
      <PageHeader
        title="Profilo"
        titleAdornment={<HelpButton screen="/profilo" />}
        description="I tuoi dati nella console e i client che lavorano a tuo nome."
      />

      {member && (
        <Paper component="section" variant="outlined" aria-labelledby="profilo-dati" sx={{ p: 4 }}>
          <Typography id="profilo-dati" variant="h6" component="h2">
            I tuoi dati
          </Typography>
          <Box
            component="dl"
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: 'max-content 1fr' },
              columnGap: 4,
              rowGap: { xs: 0.5, sm: 2 },
              mt: 3,
              mb: 0,
              '& dt': { color: 'text.secondary', mt: { xs: 2, sm: 0 } },
              '& dd': { m: 0, overflowWrap: 'anywhere' },
            }}
          >
            <dt>Nome</dt>
            <dd>{member.name}</dd>
            <dt>Email aziendale Google</dt>
            <dd>{member.email}</dd>
            <dt>Ruolo</dt>
            <dd>{ROLE_LABELS[member.role]}</dd>
            <dt>Livello</dt>
            <dd>{TEAM_LEVEL_LABELS[member.level]}</dd>
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 3 }}>
            Nome, ruolo e livello li cambia un amministratore in «Impostazioni › Team».
          </Typography>
        </Paper>
      )}

      {mcp && (
        <Paper component="section" variant="outlined" aria-labelledby="profilo-mcp" sx={{ p: 4 }}>
          <Stack spacing={0.5} sx={{ mb: 3 }}>
            <Typography id="profilo-mcp" variant="h6" component="h2">
              Connessioni di Claude e Cursor
            </Typography>
            <Typography variant="body2" color="text.secondary">
              I client che hai collegato a EasyHelp: lavorano a tuo nome e con il tuo ruolo. Revoca quelli che non usi
              più o che non riconosci.
            </Typography>
          </Stack>
          <OwnMcpConnections onRevoked={setNotice} />
        </Paper>
      )}

      <Snackbar open={Boolean(notice)} autoHideDuration={6000} onClose={() => setNotice(null)} message={notice} />
    </Stack>
  );
}
